/**
 * How each IDL message of an MCE adapter maps to an LN2 FlatBuffer message,
 * read from the translation table the adapter stores or an upload just built.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Select, Spin, Statistic, Table, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { IdlSummaryT } from "../../../../flatbuffers/generated/mce/idl-summary";
import { loadSchema, type ReflectionSchema } from "../../../../flatbuffers/reflection/reflection";
import MceEventClassEditor from "./MceEventClassEditor";
import MceOverrideEditor, { MceOverrideTarget } from "./MceOverrideEditor";
import { base64ToBytes } from "../../../../utils/base64";
import { readTranslationTable } from "../../../../utils/mceIdl/client";
import { asString, unpackMapping } from "../../../../utils/mceIdl/messages";
import {
  buildTableView,
  MceControlRow,
  MceDiagnosticRow,
  MceEventRow,
  MceMessageRow,
  MceSourceNames,
  MceTableView,
  MceTarget,
} from "../../../../utils/mceIdl/tableView";

const PAGE_SIZE = 50;
const ALL = "all";

let ownSchema: Promise<ReflectionSchema> | null = null;
function lnMessageSchema(): Promise<ReflectionSchema> {
  if (!ownSchema) {
    ownSchema = import("../../../../flatbuffers/generated/front-end/front-end-message-bfbs").then(
      (generated) => loadSchema(generated.bfbsData),
    );
  }
  return ownSchema;
}

function TargetLine({ target }: { target: MceTarget }) {
  return (
    <div className="generalStyles__mono">
      {target.when !== undefined && <span>{target.when} → </span>}
      <Tag>{target.family}</Tag>
      {target.path.length > 0 && <span>{target.path.join(" › ")} › </span>}
      <strong>{target.member}</strong>
      {target.valueType && <span> ({target.valueType})</span>}
    </div>
  );
}

function targetsCell(row: MceMessageRow) {
  if (row.ignoreReason !== null) return <span>Not sent to LN2: {row.ignoreReason}</span>;
  if (row.recordFields.length > 0) return <span>{row.recordFields.length} record fields</span>;
  if (row.targets.length === 0 && row.transform) return <span>Set by {row.transform}</span>;
  const shown = row.targets.slice(0, 3);
  return (
    <>
      {shown.map((target) => (
        <TargetLine
          key={`${target.when ?? ""}-${target.family}-${target.member}`}
          target={target}
        />
      ))}
      {row.targets.length > shown.length && (
        <span>and {row.targets.length - shown.length} more</span>
      )}
    </>
  );
}

function MessageDetail({ row }: { row: MceMessageRow }) {
  return (
    <div>
      <div>
        IDL location: {row.file}:{row.line}
      </div>
      <div>Interface: {row.iface}</div>
      {row.parentCategory && <div>Parent category: {row.parentCategory}</div>}
      {row.profileRow && <div>Profile row: {row.profileRow}</div>}
      {row.transform && <div>Transform: {row.transform}</div>}
      {row.targets.map((target) => (
        <TargetLine key={`${target.when ?? ""}-${target.member}`} target={target} />
      ))}
      {row.recordFields.map((field) => (
        <div key={field.field}>
          <strong>{field.field}</strong>
          {field.targets.map((target) => (
            <TargetLine key={`${target.when ?? ""}-${target.member}`} target={target} />
          ))}
        </div>
      ))}
    </div>
  );
}

const MESSAGE_COLUMNS: ColumnsType<MceMessageRow> = [
  { title: "Category", dataIndex: "category", width: 100 },
  {
    title: "ID",
    key: "id",
    width: 90,
    render: (_value, row) => (row.subId ? `${row.id}/${row.subId}` : row.id),
  },
  { title: "IDL message", dataIndex: "name" },
  { title: "LN2 FlatBuffer message", key: "targets", render: (_value, row) => targetsCell(row) },
  { title: "Rule", dataIndex: "ruleKind", width: 110 },
  { title: "Keys", dataIndex: "keyCount", width: 70 },
  { title: "IDL type", dataIndex: "idlType" },
  { title: "Address", dataIndex: "address", width: 120 },
];

const EVENT_COLUMNS: ColumnsType<MceEventRow> = [
  { title: "Event ID", dataIndex: "eventId", width: 90 },
  { title: "Text in the XML", dataIndex: "text" },
  {
    title: "Received as",
    dataIndex: "family",
    width: 120,
    render: (family: string) => <Tag>{family}</Tag>,
  },
  { title: "LN2 member", dataIndex: "member" },
  { title: "Source", dataIndex: "source", width: 110 },
];

function commandsCell(row: MceControlRow) {
  if (row.ignoreReason !== null) return <span>Not sent to the controller: {row.ignoreReason}</span>;
  if (row.transform) return <span>Set by {row.transform}</span>;
  return row.commands.map((command) => (
    <div key={`${command.id}-${command.subId}`}>
      <strong>{command.name || "Unnamed command"}</strong> ({command.id}
      {command.subId ? `/${command.subId}` : ""})
      {command.args.length > 0 && <span> {command.args.join(", ")}</span>}
    </div>
  ));
}

const CONTROL_COLUMNS: ColumnsType<MceControlRow> = [
  {
    title: "LN2 control",
    key: "control",
    render: (_value, row) => (row.control ? <TargetLine target={row.control} /> : null),
  },
  { title: "MCE commands", key: "commands", render: (_value, row) => commandsCell(row) },
  { title: "Rule", dataIndex: "ruleKind", width: 130 },
  {
    title: "Address",
    key: "address",
    width: 100,
    render: (_value, row) => row.commands[0]?.address ?? "",
  },
  { title: "Source", dataIndex: "source", width: 110 },
];

export const DIAGNOSTIC_COLUMNS: ColumnsType<MceDiagnosticRow> = [
  {
    title: "Severity",
    dataIndex: "severity",
    width: 100,
    render: (severity: string, row) => (
      <Tag color={row.isError ? "red" : undefined}>{severity}</Tag>
    ),
  },
  { title: "Code", dataIndex: "code", width: 190 },
  { title: "Where", dataIndex: "where", width: 240 },
  { title: "Message", dataIndex: "message" },
];

function includesText(values: (string | number)[], query: string): boolean {
  const needle = query.trim().toLowerCase();
  return !needle || values.some((value) => String(value).toLowerCase().includes(needle));
}

function optionsOf(values: string[], allLabel: string) {
  const distinct = [...new Set(values)].filter(Boolean).sort();
  return [{ value: ALL, label: allLabel }, ...distinct.map((value) => ({ value, label: value }))];
}

interface MceMappingReviewProps {
  /** The Mapping to show, as the adapter state holds it. */
  cdoBinaryBase64: string;
  /** Findings and counts of the build, when this page just ran one. */
  summary?: IdlSummaryT | null;
  diagnostics?: MceDiagnosticRow[];
  /** Set for a saved adapter: its events can then be edited. */
  adapterId?: string;
  onStoredChanged?: () => void;
}

export default function MceMappingReview({
  cdoBinaryBase64,
  summary = null,
  diagnostics = [],
  adapterId = undefined,
  onStoredChanged = undefined,
}: MceMappingReviewProps) {
  const [view, setView] = useState<MceTableView | null>(null);
  const [names, setNames] = useState<MceSourceNames | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<MceOverrideTarget | null>(null);
  const [failed, setFailed] = useState(false);
  const [mapping, setMappingFilter] = useState("mapped");
  const [category, setCategory] = useState(ALL);
  const [rule, setRule] = useState(ALL);
  const [eventFamily, setEventFamily] = useState(ALL);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let current = true;
    // The last view stays up until the new one is ready: an event write stores a new
    // mapping, and blanking here would take the open tab and its editor away.
    setFailed(false);
    (async () => {
      const stored = unpackMapping(base64ToBytes(cdoBinaryBase64));
      const [table, schema] = await Promise.all([readTranslationTable(stored), lnMessageSchema()]);
      if (!table) throw new Error("This mapping holds no translation table");
      const sources: MceSourceNames = {
        main: asString(stored.idl?.main?.name),
        controller: asString(stored.idl?.controller?.name),
        includes: (stored.idl?.includes ?? []).map((file) => asString(file.name)),
        eventsXml: asString(stored.idl?.eventsXml?.name),
      };
      return { built: buildTableView(table, schema, sources), sources };
    })()
      .then(({ built, sources }) => {
        if (current) {
          setView(built);
          setNames(sources);
        }
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [cdoBinaryBase64]);

  const messages = useMemo(
    () =>
      (view?.messages ?? []).filter(
        (row) =>
          (mapping === ALL || row.mapped === (mapping === "mapped")) &&
          (category === ALL || row.category === category) &&
          (rule === ALL || row.ruleKind === rule) &&
          includesText(
            [row.name, row.id, row.idlType, ...row.targets.map((target) => target.member)],
            query,
          ),
      ),
    [view, mapping, category, rule, query],
  );
  const events = useMemo(
    () =>
      (view?.events ?? []).filter(
        (row) =>
          (eventFamily === ALL || row.family === eventFamily) &&
          includesText([row.eventId, row.text, row.member], query),
      ),
    [view, eventFamily, query],
  );

  const controls = (view?.controls ?? []).filter((row) =>
    includesText(
      [
        row.control?.member ?? "",
        row.transform ?? "",
        ...row.commands.flatMap((command) => [command.name, command.id]),
      ],
      query,
    ),
  );

  if (failed) {
    return (
      <Alert
        type="error"
        showIcon
        message="This mapping could not be shown. Reload the page and try again."
      />
    );
  }
  if (!view) return <Spin tip="Reading the mapping" />;

  const overrideColumn = <T extends MceMessageRow | MceControlRow>(
    kind: MceOverrideTarget["kind"],
  ): ColumnsType<T> =>
    adapterId
      ? [
          {
            title: "",
            key: "override",
            width: 100,
            render: (_value, row) => (
              <Button
                size="small"
                type="link"
                onClick={(event) => {
                  event.stopPropagation();
                  setOverrideTarget({ kind, row } as MceOverrideTarget);
                }}
              >
                {row.override ? "Overridden" : "Override"}
              </Button>
            ),
          },
        ]
      : [];

  const mappedCount = view.messages.filter((row) => row.mapped).length;
  return (
    <div data-testid="mce-mapping-review">
      <div className="d-flex flex-wrap gap-4 mt-3 mb-3">
        <Statistic title="IDL messages" value={view.messages.length} />
        <Statistic title="Mapped to LN2" value={mappedCount} />
        <Statistic title="Unmapped" value={view.messages.length - mappedCount} />
        <Statistic title="Events" value={view.events.length} />
        {summary && <Statistic title="Validator errors" value={summary.errors} />}
        {summary && <Statistic title="Validator warnings" value={summary.warnings} />}
      </div>
      <Input.Search
        allowClear
        aria-label="Search the mapping"
        placeholder="Search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <Tabs
        items={[
          {
            key: "messages",
            label: `Messages (${view.messages.length})`,
            children: (
              <>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Select
                    aria-label="Mapping status"
                    value={mapping}
                    onChange={setMappingFilter}
                    options={[
                      { value: ALL, label: "All" },
                      { value: "mapped", label: "Mapped" },
                      { value: "unmapped", label: "Unmapped" },
                    ]}
                  />
                  <Select
                    aria-label="Category"
                    value={category}
                    onChange={setCategory}
                    options={optionsOf(
                      view.messages.map((row) => row.category),
                      "All categories",
                    )}
                  />
                  <Select
                    aria-label="Rule"
                    value={rule}
                    onChange={setRule}
                    options={optionsOf(
                      view.messages.map((row) => row.ruleKind),
                      "All rules",
                    )}
                  />
                </div>
                <Table
                  size="small"
                  rowKey="key"
                  columns={[...MESSAGE_COLUMNS, ...overrideColumn<MceMessageRow>("message")]}
                  dataSource={messages}
                  pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
                  expandable={{
                    expandedRowRender: (row) => <MessageDetail row={row} />,
                  }}
                  scroll={{ x: true }}
                />
              </>
            ),
          },
          {
            key: "events",
            label: `Events (${view.events.length})`,
            children: adapterId ? (
              <MceEventClassEditor
                adapterId={adapterId}
                storedBase64={cdoBinaryBase64}
                onStoredChanged={onStoredChanged ?? (() => undefined)}
              />
            ) : (
              <>
                <Select
                  className="mb-2"
                  aria-label="Received as"
                  value={eventFamily}
                  onChange={setEventFamily}
                  options={optionsOf(
                    view.events.map((row) => row.family),
                    "All classes",
                  )}
                />
                <Table
                  size="small"
                  rowKey="key"
                  columns={EVENT_COLUMNS}
                  dataSource={events}
                  pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
                  scroll={{ x: true }}
                />
              </>
            ),
          },
          {
            key: "commands",
            label: `Commands (${view.controls.length})`,
            children: (
              <Table
                size="small"
                rowKey="key"
                columns={[...CONTROL_COLUMNS, ...overrideColumn<MceControlRow>("control")]}
                dataSource={controls}
                pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
                scroll={{ x: true }}
              />
            ),
          },
          {
            key: "validator",
            label: `Validator (${diagnostics.length})`,
            children: (
              <Table
                size="small"
                rowKey="key"
                columns={DIAGNOSTIC_COLUMNS}
                dataSource={diagnostics}
                pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
                scroll={{ x: true }}
              />
            ),
          },
        ]}
      />
      {overrideTarget && adapterId && names && (
        <MceOverrideEditor
          target={overrideTarget}
          adapterId={adapterId}
          cdoBinaryBase64={cdoBinaryBase64}
          names={names}
          onClose={() => setOverrideTarget(null)}
          onSaved={() => onStoredChanged?.()}
        />
      )}
    </div>
  );
}
